import { useCallback, useEffect, useState } from 'react'
import { fetchBrandKit, patchBrandKit } from '../api/brand-kit-api'
import { useBrandKitStore } from '../store/brand-kit-store'
import type { BrandKit } from '../types'

/**
 * Fetches brand kit from API on mount. Exposes savePartial for manual (e.g. per-section) save.
 *
 * `enabled` (default true) gates the automatic fetch — the onboarding
 * conversation panel mounts before its business exists, and an eager fetch
 * there is a guaranteed 403. The fetch fires as soon as `enabled` flips true.
 */
export function useBrandKitApi({ enabled = true }: { enabled?: boolean } = {}) {
  const setBrandKitFromApi = useBrandKitStore(s => s.setBrandKitFromApi)
  const [loading, setLoading] = useState(true)

  const savePartial = useCallback(async (partial: Partial<BrandKit>): Promise<void> => {
    // Exclude logo and images from auto-save — both are saved explicitly via their
    // own upload/remove flows to preserve attachment IDs and avoid race conditions
    const { logoId: _logoId, images: _images, ...withoutExplicit } = partial
    const updated = await patchBrandKit(withoutExplicit)
    const { logoId: _responseLogoId, ...withoutResponseLogo } = updated
    useBrandKitStore.getState().updateBrandKit(withoutResponseLogo)
  }, [])

  const refetch = useCallback(
    async (opts?: { silent?: boolean }): Promise<void> => {
      const silent = opts?.silent ?? false
      if (!silent) setLoading(true)
      try {
        const data = await fetchBrandKit()
        setBrandKitFromApi(data)
      } catch {
        // Keep existing store (e.g. from localStorage) on error
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [setBrandKitFromApi]
  )

  useEffect(() => {
    if (!enabled) return
    refetch()
  }, [refetch, enabled])

  return { loading, savePartial, refetch }
}
